import {
  useGetPinnedProjectStates,
  useGetRecentProjectStates,
  useSetCurrentProjectState,
  useSetPinProjectState,
} from '@api/user-settings';
import { ProjektstadiumType } from '@models/project';
import { UserSettingsProjectState } from '@models/user-settings';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useUserSettings } from '@providers';
import { typedMemo, useDelayedBoolean } from '@utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styles from './ProjectSelector.module.scss';
import { ProjectsList } from './ProjectsList';

export const ProjectSelector = typedMemo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const userSettings = useUserSettings();
  const setCurrentProject = useSetCurrentProjectState();
  const setPinProjectState = useSetPinProjectState();
  const hasLoadedOnceRef = useRef(false);
  const currentProject =
    userSettings.value?.einstellungen?.aktuellesProjektstadium;
  const organisationId =
    userSettings.value?.aktuelleOrganisation?.organisationId;
  const maxPinnedProjects = 10;
  const shouldLoadData = isOpen || hasLoadedOnceRef.current;

  const { result: pinnedProjectsFromApi = [], loading: pinnedProjectsLoading } =
    useGetPinnedProjectStates(organisationId, { enabled: shouldLoadData });

  const {
    result: recentProjectsFromApi = [],
    loading: recentProjectsLoading,
    refetch: refetchRecentProjects,
  } = useGetRecentProjectStates(organisationId, { enabled: shouldLoadData });

  const showPinnedSkeleton = useDelayedBoolean(pinnedProjectsLoading, 150);
  const showRecentSkeleton = useDelayedBoolean(recentProjectsLoading, 150);

  const [optimisticPinnedProjects, setOptimisticPinnedProjects] = useState<
    UserSettingsProjectState[]
  >([]);

  useEffect(() => {
    setOptimisticPinnedProjects(pinnedProjectsFromApi);
  }, [pinnedProjectsFromApi]);

  useEffect(() => {
    if (currentProject?.projektstadiumId && hasLoadedOnceRef.current) {
      refetchRecentProjects();
    }
  }, [currentProject?.projektstadiumId, refetchRecentProjects]);

  const handleExpandClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    hasLoadedOnceRef.current = true;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSelect = async (
    projectState: UserSettingsProjectState,
  ) => {
    handleClose();
    if (projectState) {
      try {
        await setCurrentProject.mutate(projectState);
      } finally {
        refetchRecentProjects();
      }
    }
  };

  const handleProjectPin = (
    project: UserSettingsProjectState,
    shouldPin: boolean,
  ) => {
    const projektstadiumId = project.projektstadiumId;
    if (!projektstadiumId || !organisationId) return;

    const previousPinnedProjects = [...optimisticPinnedProjects];
    if (shouldPin) {
      setOptimisticPinnedProjects((prev) => [project, ...prev]);
    } else {
      setOptimisticPinnedProjects((prev) =>
        prev.filter((p) => p.projektstadiumId !== projektstadiumId),
      );
    }
    setPinProjectState
      .mutate(undefined, {
        projektstadiumId,
        organisationId,
        istAngeheftet: shouldPin.toString(),
      })
      .catch(() => {
        setOptimisticPinnedProjects(previousPinnedProjects);
      });
  };

  const handleShowAllProjects = () => {
    navigate('/projects');
    handleClose();
  };

  const isPinned = (project: UserSettingsProjectState) => {
    const projektstadiumId = project.projektstadiumId;
    return optimisticPinnedProjects.some(
      (p: UserSettingsProjectState) => p.projektstadiumId === projektstadiumId,
    );
  };

  const canPin = optimisticPinnedProjects.length < maxPinnedProjects;

  const firstLineRef = useRef<HTMLSpanElement>(null);
  const secondLineRef = useRef<HTMLSpanElement>(null);

  const secondaryParts = currentProject
    ? [
        currentProject.projektstadiumnummer,
        ProjektstadiumType[currentProject.stadiumTyp!],
      ].filter(Boolean)
    : [];
  const secondLineText = secondaryParts.join(' | ');
  const fullTitle = currentProject
    ? `${currentProject.kurzname} ${secondLineText}`
    : t('projectSelector.noProjectSelected');

  const isTruncated =
    (firstLineRef.current &&
      firstLineRef.current.scrollWidth > firstLineRef.current.clientWidth) ||
    (secondLineRef.current &&
      secondLineRef.current.scrollWidth > secondLineRef.current.clientWidth);

  return (
    <>
      <Tooltip
        title={isTruncated ? fullTitle : ''}
        placement="top"
        disableInteractive
        enterDelay={500}
        enterNextDelay={500}
      >
        <Button
          variant="text"
          color="inherit"
          endIcon={<ExpandMoreOutlined />}
          onClick={handleExpandClick}
          className={styles.projectSelector}
          data-testid="project-selector-button"
          sx={{
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
          }}
        >
          <div className={styles.currentProject}>
            <Typography
              className={styles.projectTitle}
              ref={firstLineRef}
              data-testid="project-selector-title"
            >
              {currentProject?.kurzname ||
                t('projectSelector.noProjectSelected')}
            </Typography>
            {currentProject && (
              <Typography
                variant="body2"
                className={styles.projectTitle2}
                ref={secondLineRef}
                data-testid="project-selector-title2"
              >
                {secondLineText}
              </Typography>
            )}
          </div>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            className: styles.dropdownMenu,
            sx: {
              minWidth: 380,
              maxWidth: 500,
              width: 420,
              minHeight: 124,
              maxHeight: 696,
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {/* Pinned Projects Section */}
        <ProjectsList
          projects={optimisticPinnedProjects}
          showSkeleton={showPinnedSkeleton}
          onProjectSelect={handleProjectSelect}
          onProjectPin={handleProjectPin}
          isPinned={isPinned}
          listType="pinned"
          canPin={canPin}
          currentProjectId={currentProject?.projektstadiumId}
        />
        {/* Recent Projects Section */}
        <ProjectsList
          projects={recentProjectsFromApi}
          showSkeleton={showRecentSkeleton}
          onProjectSelect={handleProjectSelect}
          onProjectPin={handleProjectPin}
          isPinned={isPinned}
          listType="recent"
          canPin={canPin}
          currentProjectId={currentProject?.projektstadiumId}
        />

        {/* Show All Projects */}
        <Button variant="text" fullWidth onClick={handleShowAllProjects}>
          {t('projectSelector.showAllProjects')}
        </Button>
      </Menu>
    </>
  );
});
